import React, { lazy, Suspense, useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Analytics } from '@vercel/analytics/react';
import './index.css';
import LandingPage from './LandingPage';
import reportWebVitals from './reportWebVitals';

// Lazy load App to prevent loading 40KB+ of JS, 50KB JSON, and firing API calls
// for users who are just browsing the landing page.
const App = lazy(() => import(/* webpackChunkName: "app" */ './App'));

const AppLoadingFallback = () => (
  <div className="App">
    <div className="app-orb app-orb-one" />
    <div className="app-orb app-orb-two" />
    <section className="status-card" aria-live="polite" role="status">
      <div className="status-orb" />
      <p>Loading StudyHub…</p>
    </section>
  </div>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
const MainWrapper = () => {
  const getRoute = () => window.location.pathname.replace(/\/+$/, '').startsWith('/app') ? 'app' : 'landing';
  const [route, setRoute] = useState(getRoute());
  
  // Track if they've ever opened the app. If true, we keep it mounted
  // to avoid losing state when navigating back to the landing page.
  const [hasVisitedApp, setHasVisitedApp] = useState(route === 'app');

  useEffect(() => {
    const handlePopState = () => setRoute(getRoute());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (route === 'app') setHasVisitedApp(true);
  }, [route]);

  // Prefetch the App chunk and the initial data immediately when on landing page
  useEffect(() => {
    if (route === 'landing') {
      import(/* webpackChunkName: "app" */ './App').catch(() => {});
      // Dynamically import db service to pre-fetch subjects and initial resources
      import('./services/db').then(mod => {
        mod.getSubjects().then(subjects => {
          if (!subjects || subjects.length === 0) return;
          
          // Replicate App.js default branch selection logic to pre-fetch the exact first view
          const sortSubjects = (items) => [...items].sort((a, b) => {
            const d = (b.semester ?? 0) - (a.semester ?? 0);
            return d !== 0 ? d : a.name.localeCompare(b.name);
          });
          
          const sorted = sortSubjects(subjects);
          let bestDefault = sorted.find(s => (s.branch === 'COE' ? 'CSE' : s.branch) === 'CSE') || sorted[0];
          
          const defaultId = bestDefault?._id ?? bestDefault?.id;
          if (defaultId) mod.getResourcesForSubject(defaultId).catch(() => {});
        }).catch(() => {});
      }).catch(() => {});
    }
  }, [route]);

  const navigateTo = (targetRoute, url) => (e) => {
    if (e) e.preventDefault();
    window.history.pushState({}, '', url);
    setRoute(targetRoute);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <div style={{ display: route === 'landing' ? 'block' : 'none' }}>
        <LandingPage isActive={route === 'landing'} onNavigate={navigateTo('app', '/app')} />
      </div>
      {hasVisitedApp && (
        <div style={{ display: route === 'app' ? 'block' : 'none' }}>
          <Suspense fallback={<AppLoadingFallback />}>
            <App isActive={route === 'app'} onNavigate={navigateTo('landing', '/')} />
          </Suspense>
        </div>
      )}
    </>
  );
};

root.render(
  <React.StrictMode>
    <MainWrapper />
    <Analytics />
  </React.StrictMode>
);

reportWebVitals();
